/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useWindowDimension from "../../components/hooks/useWindowDimension";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import {
  clubListResponce,
  createClubResponce,
  updateClubResponce,
} from "../../store/features/club/clubSlice";
import clubService from "../../store/features/services/clubService";
import {
  extractClubIdFromPayload,
  persistBrandingGalleryViaWebsite,
} from "../../helpers/brandingGallery";
import { showToast } from "../../store/features/toast/toastSlice";
import ClubFormModal from "./clubFormModal";
import ClubManagementComponent from "./clubManagementComponent";

const ClubManagementContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const smarttableref = useRef();
  const clubList = useSelector((state) => state?.club?.clubList ?? []);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [placeHolder, setPlaceHolder] = useState(true);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const pageSizeRef = useRef(15);
  const tableContainerRef = useRef(null);
  const dimensions = useWindowDimension();
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [loaderHtml, setLoaderHtmlHeight] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingClub, setEditingClub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createLoader, setCreateLoader] = useState(false);

  const getList = () => {
    dispatch(clubListResponce({ limit: 1000, page: 1 })).then((resultAction) => {
      if (clubListResponce.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (clubListResponce.rejected.match(resultAction)) {
        setPlaceHolder(false);
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message || t("CLUB_LIST_ERROR"),
            type: "error",
          })
        );
      }
    });
  };

  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    if (!Array.isArray(clubList)) return;
    const q = searchValue?.toLowerCase()?.trim();
    if (!q) {
      setFilteredData(clubList);
      return;
    }
    setFilteredData(
      clubList.filter(
        (c) =>
          c?.name?.toLowerCase().includes(q) ||
          c?.slug?.toLowerCase().includes(q)
      )
    );
  }, [searchValue, clubList]);

  const onTableFilterChange = (filterText) => {
    setSearchValue(filterText);
  };

  const searchFilterClear = () => {
    smarttableref.current?.querySelectorAll("button")[1]?.click();
  };

  const columns = [
    {
      label: (
        <>
          {t("CLUB_NAME")}{" "}
          <span className="thCount">
            {searchValue == null ? clubList?.length : filteredData?.length}
          </span>
        </>
      ),
      key: "name",
      filter: false,
      sorting: false,
    },
    {
      label: t("CLUB_SLUG"),
      key: "slug",
      filter: false,
      sorting: false,
    },
    {
      label: t("CLUB_STATUS_COL"),
      key: "isactive",
      filter: false,
      sorting: false,
    },
    {
      label: t("CLUB_BRANDING_COL"),
      key: "branding",
      filter: false,
      sorting: false,
    },
    {
      label: t("ACTIONS"),
      key: "actions",
      filter: false,
      sorting: false,
    },
  ];

  const openCreateModal = () => {
    setCreateLoader(true);
    setModalMode("add");
    setEditingClub(null);
    setTimeout(() => {
      setShowModal(true);
      setCreateLoader(false);
    }, 150);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClub(null);
  };

  const onSave = async (body) => {
    const gallery = Array.isArray(body.brandinggallery) ? body.brandinggallery : [];
    const billingDetails = body.billingDetails;
    const clubPayload = { ...body };
    delete clubPayload.brandinggallery;
    delete clubPayload.billingDetails;

    setSaving(true);
    try {
      if (modalMode === "add") {
        const resultAction = await dispatch(createClubResponce(clubPayload));
        if (!createClubResponce.fulfilled.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message:
                resultAction.payload ||
                resultAction?.error?.message ||
                t("CLUB_SAVE_ERROR"),
              type: "error",
            })
          );
          return;
        }
        const newId = extractClubIdFromPayload(resultAction.payload);
        if (newId) {
          await persistBrandingGalleryViaWebsite(newId, gallery, {
            alreadyResolved: true,
          });
          if (billingDetails && typeof billingDetails === "object") {
            await clubService.updateClubWebsite(newId, billingDetails);
          }
        }
        dispatch(
          showToast({
            show: false,
            message:
              resultAction?.payload?.message || t("CLUB_CREATE_SUCCESS"),
            type: "success",
          })
        );
        closeModal();
        getList();
      } else {
        const clubId = editingClub?._id ?? editingClub?.id;
        if (!clubId) {
          throw new Error(t("CLUB_SAVE_ERROR"));
        }
        const resultAction = await dispatch(
          updateClubResponce({ id: clubId, body: { ...clubPayload, brandinggallery: gallery } })
        );
        if (!updateClubResponce.fulfilled.match(resultAction)) {
          dispatch(
            showToast({
              show: false,
              message:
                resultAction.payload ||
                resultAction?.error?.message ||
                t("CLUB_SAVE_ERROR"),
              type: "error",
            })
          );
          return;
        }
        await persistBrandingGalleryViaWebsite(clubId, gallery, {
          alreadyResolved: true,
        });
        if (billingDetails && typeof billingDetails === "object") {
          await clubService.updateClubWebsite(clubId, billingDetails);
        }
        dispatch(
          showToast({
            show: false,
            message:
              resultAction?.payload?.message || t("CLUB_UPDATE_SUCCESS"),
            type: "success",
          })
        );
        closeModal();
        getList();
      }
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SAVE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      pageSizeRef.current += 10;
      setItemsPerPage(pageSizeRef.current);
      setLoading(false);
    }, 500);
  };

  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      const data = searchValue == null ? clubList : filteredData;
      if (isAtBottom && data?.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  useEffect(() => {
    loading &&
      setLoaderHtmlHeight(
        document.getElementsByClassName("table-responsive")[0]?.offsetHeight
      );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0]?.offsetHeight
    );
  }, [loading]);

  const pageTitle = [
    { title: t("EVENTS"), path: "/events-list" },
    { title: t("CLUB_MANAGEMENT"), path: "/clubs-management" },
  ];

  const displayRows =
    searchValue == null
      ? clubList || []
      : filteredData;
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  return (
    <>
      <ClubManagementComponent
        smarttableref={smarttableref}
        columns={columns}
        clubData={displayRows}
        placeHolderShimer={placeHolder}
        onTableFilterChange={onTableFilterChange}
        searchFilterClear={searchFilterClear}
        searchValue={searchValue}
        dimensions={dimensions}
        breadcrumbHeight={breadcrumbHeight}
        loading={loading}
        tableContainerRef={tableContainerRef}
        loaderHtml={loaderHtml}
        itemsPerPage={Math.max(itemsPerPage, displayRows.length || 0)}
        pageTitle={pageTitle}
        openCreateModal={openCreateModal}
        createLoader={createLoader}
        emptyImg={CLOUDINARY_URL + "v7vqwdvpm1sxnpfxov7g.png"}
      />
      <ClubFormModal
        showModal={showModal}
        onClose={closeModal}
        mode={modalMode}
        initialClub={editingClub}
        onSave={onSave}
        saving={saving}
        detailLoading={false}
      />
    </>
  );
};

export default ClubManagementContainer;
